import React  from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import DatePicker from 'react-modern-calendar-datepicker';
import { convertDateToModernCalendarFormat, formatDateString } from '../utils/dates';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import { huLocale } from '../config/datepicker-locales';
import { deLocale } from '../config/datepicker-locales';
import {
  startOfYear,
  subYears,
  endOfYear,
  subWeeks,
  addWeeks
} from 'date-fns';
import { SUPPORTED_LANGUAGES } from '../utils/const';

const { EN, DE } = SUPPORTED_LANGUAGES;

const DateRangePicker = ({
  selectedDateRange,
  setSelectedDateRange,
  ...props
}) => {
  const { t, i18n } = useTranslation();

  const now = new Date();
  const lastYear = subYears(now, 1);

  const thisYearValue = {
    from: convertDateToModernCalendarFormat(startOfYear(now)),
    to: convertDateToModernCalendarFormat(endOfYear(now))
  };

  const lastYearValue = {
    from: convertDateToModernCalendarFormat(startOfYear(lastYear)),
    to: convertDateToModernCalendarFormat(endOfYear(lastYear))
  };

  const lastWeekValue = {
    from: convertDateToModernCalendarFormat(subWeeks(now, 1)),
    to: convertDateToModernCalendarFormat(now)
  };

  const next2WeeksValue = {
    from: convertDateToModernCalendarFormat(now),
    to: convertDateToModernCalendarFormat(addWeeks(now, 2))
  };

  const handleOnChange = value => e => {
    setSelectedDateRange(value);
    e.target.blur();
  };

  const renderCustomInput = ({ ref }) => (
    <input
      readOnly
      ref={ ref }
      placeholder={ t('objective.filtering.dateRange.label') }
      value={ selectedDateRange.from && selectedDateRange.to ?
        `${formatDateString(selectedDateRange.from)} - ${formatDateString(selectedDateRange.to)}` :
        '' }
      style={ {
        textAlign: 'center',
        padding: '0.5rem 0.5rem',
        width: '18rem',
        letterSpacing: '0.03rem',
        fontSize: '1rem',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        color: 'currentColor',
        outline: 'none'
      } }
      className="date-range-picker-input"
    />
  );

  const renderCustomFooter = () => (
    <div
      className="datePicker-custom-footer"
      style={ {
        display: 'flex',
        flexDirection: 'column',
        gridGap: 8,
        padding: 8
      } }
    >
      <Button
        onClick={ handleOnChange(thisYearValue) }
        variant='outlined'
        color='primary'
        className='this-year'
      >
        {t('objective.filtering.dateRange.thisYear')}
      </Button>
      <Button
        onClick={ handleOnChange(lastYearValue) }
        variant='outlined'
        color='primary'
        className='last-year'
      >
        {t('objective.filtering.dateRange.lastYear')}
      </Button>
      <Button
        onClick={ handleOnChange(lastWeekValue) }
        variant='outlined'
        color='primary'
        className='last-week'
      >
        {t('objective.filtering.dateRange.lastWeek')}
      </Button>
      <Button
        onClick={ handleOnChange(next2WeeksValue) }
        variant='outlined'
        color='primary'
        className='next2-weeks'
      >
        {t('objective.filtering.dateRange.next2Weeks')}
      </Button>
    </div>
  );

  return (
    <DatePicker
      value={ selectedDateRange }
      shouldHighlightWeekends
      colorPrimary='#99B64E'
      onChange={ setSelectedDateRange }
      renderInput={ renderCustomInput }
      renderFooter={ renderCustomFooter }
      locale={ i18n.language === EN ? EN :  i18n.language === DE ? deLocale : huLocale }
      calendarPopperPosition='bottom'
      { ...props }
    />
  );
};

export default DateRangePicker;
